import {
    baselineValueForField,
    calculatePricingValueChangeRatio,
} from '@/utils/priceListPricingFieldBaselines';
import {
    DEFAULT_PRICING_SETTINGS,
    builderFormToCalculatorInput,
    calculatePriceAdjustmentRatio,
    calculateProductPricing,
    getDefaultFurnizorPricing,
    mergeFormWithFurnizorPricing,
    parseNumericInput,
    setDefaultFurnizorPricing,
} from '@/utils/priceListProductCalculator';
import { builderFormToPricingDetailPayload } from '@/utils/pricingDetailMapper';
import { TRANSPORT_COST_MODE_FORMULA } from '@/utils/transportCostBreakdown';
import { cloneDeep } from 'lodash';

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function toNullableNumber(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const num = parseNumericInput(value);
    return Number.isFinite(num) ? num : null;
}

/**
 * Draft-table "preț vechi" source for Preț intrare RON cu TVA:
 * open-session baselines frozen at the published/lista-veche product
 * (not last-în-lucru / Valoare veche, which advances on reopen+save).
 *
 * @param {Object|null|undefined} builderState
 * @param {string|null|undefined} tabKey
 * @returns {number|null}
 */
export function resolveEntryPriceRonIncVatOldFromBaselines(builderState, tabKey) {
    if (!builderState || !tabKey) {
        return null;
    }
    const session = builderState.pricingFieldBaselinesByTab?.[tabKey];
    return toNullableNumber(
        baselineValueForField('entryPriceRonIncVat', session),
    );
}

/**
 * Draft-table "preț vechi" source for Preț intrare RON fără TVA.
 *
 * @param {Object|null|undefined} builderState
 * @param {string|null|undefined} tabKey
 * @returns {number|null}
 */
export function resolveEntryPriceRonExVatOldFromBaselines(builderState, tabKey) {
    if (!builderState || !tabKey) {
        return null;
    }
    const session = builderState.pricingFieldBaselinesByTab?.[tabKey];
    return toNullableNumber(
        baselineValueForField('entryPriceRonExVat', session),
    );
}

/**
 * Draft-table previous sale/list price from open-session baselines
 * (lista veche), not last-în-lucru Valoare veche baselines.
 *
 * @param {Object|null|undefined} builderState
 * @param {string|null|undefined} tabKey
 * @returns {number|null}
 */
export function resolveListPricePreviousFromBaselines(builderState, tabKey) {
    if (!builderState || !tabKey) {
        return null;
    }
    const session = builderState.pricingFieldBaselinesByTab?.[tabKey];
    return toNullableNumber(
        baselineValueForField('listPriceRonIncVatFinal', session),
    );
}

/**
 * Draft-table "preț listă vechi furnizor" from open-session baselines
 * (lista veche). Form.supplierListPriceOld is often normalized equal to new
 * after draft save; last-în-lucru baselines must not be used here.
 *
 * @param {Object|null|undefined} builderState
 * @param {string|null|undefined} tabKey
 * @returns {number|null}
 */
export function resolveSupplierListPriceOldFromBaselines(builderState, tabKey) {
    if (!builderState || !tabKey) {
        return null;
    }
    const session = builderState.pricingFieldBaselinesByTab?.[tabKey];
    return toNullableNumber(
        baselineValueForField('supplierListPriceNew', session),
    );
}

/**
 * @param {Object|null|undefined} furnizorTab
 * @param {Object|null|undefined} form
 * @param {Object|null|undefined} builderState
 * @param {Object|null|undefined} liveItem
 * @returns {{
 *   supplier_list_price_old: number|null,
 *   supplier_list_price_new: number|null,
 *   supplier_list_price_adjustment_ratio: number|null,
 *   currency: string,
 *   is_default: boolean,
 * }}
 */
function resolveSupplierListDisplayFields(
    furnizorTab = null,
    form = null,
    builderState = null,
    liveItem = null,
) {
    const tab = furnizorTab
        || (form ? getDefaultFurnizorPricing(form) : null)
        || form?.furnizorPricings?.[0]
        || null;
    const liveDetail = liveItem?.pricing_detail || null;
    const liveTabs = Array.isArray(liveDetail?.furnizorPricings)
        ? liveDetail.furnizorPricings
        : [];
    const liveTab = tab
        ? findMatchingLiveFurnizorTab(tab, liveTabs)
        : (liveTabs.find((row) => row?.isDefault) || liveTabs[0] || null);
    const source = tab || liveTab || liveDetail || null;

    const tabKey = String(tab?.clientKey || liveTab?.clientKey || '').trim();
    const supplierNew = toNullableNumber(
        source?.supplierListPriceNew ?? liveDetail?.supplierListPriceNew,
    );
    // Lista veche first: published live tab, then frozen open baselines.
    // Do not use draft form old (normalized after save) or last-în-lucru.
    const supplierOld = toNullableNumber(
        liveTab?.supplierListPriceNew ?? liveTab?.supplierListPriceOld,
    )
        ?? resolveSupplierListPriceOldFromBaselines(builderState, tabKey)
        ?? toNullableNumber(
            liveDetail?.supplierListPriceNew ?? liveDetail?.supplierListPriceOld,
        )
        ?? toNullableNumber(source?.supplierListPriceOld)
        ?? supplierNew;

    const currencyRaw = String(
        source?.currency
            ?? form?.currency
            ?? liveDetail?.currency
            ?? 'ron',
    ).trim().toLowerCase() || 'ron';

    const isDefault = tab
        ? Boolean(tab.isDefault)
        : liveTab
            ? Boolean(liveTab.isDefault)
            : true;

    return {
        supplier_list_price_old: supplierOld,
        supplier_list_price_new: supplierNew,
        supplier_list_price_adjustment_ratio: calculatePricingValueChangeRatio(
            supplierNew,
            supplierOld,
        ),
        currency: currencyRaw,
        is_default: isDefault,
    };
}

/**
 * Fields compared when deciding if a furnizor tab was modified vs live.
 * Keep aligned with `furnizorPricingFormToPayload` — do not include form-only
 * fields that are omitted on save (those caused false „Actualizare” rows).
 *
 * Intentionally omit derived outputs rewritten on open/save for every tab:
 * - `markupActual` (normalizeDerivedMarkupActualOnAllFurnizorTabs)
 * - formula-auto `transportCostRate` (green calculator; compared separately and skipped)
 * @type {readonly string[]}
 */
const FURNIZOR_TAB_COMPARE_FIELDS = Object.freeze([
    'supplierListPriceNew',
    'currency',
    'discount1',
    'discount2',
    'cashDiscount',
    'marketingDiscount',
    'transportCostRate',
    'markupReference',
    'overrideListPriceRonIncVatFinalValue',
    'furnizorId',
    'furnizorCode',
    'furnizorName',
    'usesManufacturerAsFurnizor',
    'isDefault',
]);

/** Ratio fields stored as decimals; form round-trips via 2-decimal %. */
const FURNIZOR_TAB_RATIO_COMPARE_FIELDS = new Set([
    'discount1',
    'discount2',
    'cashDiscount',
    'marketingDiscount',
    'transportCostRate',
    'markupReference',
]);

/**
 * @param {Object|null|undefined} tab
 * @returns {boolean}
 */
function isFormulaAutoTransportRate(tab) {
    const breakdown = tab?.transportCostBreakdown;
    if (!breakdown || typeof breakdown !== 'object') {
        return false;
    }
    return breakdown.mode === TRANSPORT_COST_MODE_FORMULA
        && !breakdown.rateIsManual;
}

/**
 * Ratio equality that tolerates null/''/0 and 2-decimal % round-trip noise
 * (e.g. live 0.023456 vs payload 0.0235 after formula / form save).
 *
 * @param {unknown} left
 * @param {unknown} right
 * @returns {boolean}
 */
function areComparableRatesEqual(left, right) {
    const leftEmpty = left === null || left === undefined || left === '';
    const rightEmpty = right === null || right === undefined || right === '';
    if (leftEmpty && rightEmpty) {
        return true;
    }
    const leftNum = leftEmpty ? 0 : parseNumericInput(left);
    const rightNum = rightEmpty ? 0 : parseNumericInput(right);
    if (!Number.isFinite(leftNum) || !Number.isFinite(rightNum)) {
        return normalizeComparableNumber(left) === normalizeComparableNumber(right);
    }
    const abs = Math.abs(leftNum - rightNum);
    // ~0.005 percentage points after ratio↔%↔ratio (2 decimals on the form).
    return abs <= 5e-5;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeComparableScalar(value) {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'boolean') {
        return value ? '1' : '0';
    }
    return String(value).trim().toLowerCase();
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeComparableNumber(value) {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    const num = parseNumericInput(value);
    return Number.isFinite(num) ? String(num) : normalizeComparableScalar(value);
}

/**
 * Live furnizor tabs for draft change detection. Falls back to a single
 * synthetic tab from flat pricing_detail / item columns (legacy products).
 *
 * @param {Object|null|undefined} liveItem
 * @returns {Object[]}
 */
export function resolveLiveFurnizorTabsForDraftCompare(liveItem) {
    if (!liveItem || typeof liveItem !== 'object') {
        return [];
    }
    const detail = liveItem.pricing_detail;
    const tabs = Array.isArray(detail?.furnizorPricings)
        ? detail.furnizorPricings
        : [];
    if (tabs.length) {
        return tabs;
    }
    if (!detail || typeof detail !== 'object') {
        const furnizorLabel = String(liveItem.furnizor || '').trim();
        if (
            liveItem.supplier_list_price == null
            && liveItem.price == null
            && !furnizorLabel
        ) {
            return [];
        }
        return [{
            isDefault: true,
            furnizorName: furnizorLabel,
            supplierListPriceNew: liveItem.supplier_list_price,
            currency: liveItem.currency,
            markupReference: liveItem.markup_reference,
            markupActual: liveItem.markup_actual,
            listPriceRonIncVatFinal: liveItem.price,
            entryPriceRonExVat: liveItem.entry_price_ron_ex_vat,
        }];
    }
    return [{
        ...detail,
        isDefault: true,
        furnizorId: detail.furnizorId ?? null,
        furnizorCode: detail.furnizorCode || '',
        furnizorName: String(
            detail.furnizorName
                || liveItem.furnizor
                || '',
        ).trim(),
        supplierListPriceNew:
            detail.supplierListPriceNew
            ?? liveItem.supplier_list_price,
        currency: detail.currency ?? liveItem.currency,
        markupReference: detail.markupReference ?? liveItem.markup_reference,
        markupActual: detail.markupActual ?? liveItem.markup_actual,
        listPriceRonIncVatFinal:
            detail.listPriceRonIncVatFinal
            ?? liveItem.price,
    }];
}

/**
 * @param {Object|null|undefined} tab
 * @param {Object[]|null|undefined} liveTabs
 * @returns {Object|null}
 */
export function findMatchingLiveFurnizorTab(tab, liveTabs = []) {
    const rows = Array.isArray(liveTabs) ? liveTabs : [];
    if (!tab || !rows.length) {
        return null;
    }
    if (tab.id != null && tab.id !== '') {
        const byId = rows.find((row) => row?.id != null && String(row.id) === String(tab.id));
        if (byId) {
            return byId;
        }
    }
    if (tab.furnizorId != null && tab.furnizorId !== '') {
        const byFurnizorId = rows.find(
            (row) => row?.furnizorId != null && String(row.furnizorId) === String(tab.furnizorId),
        );
        if (byFurnizorId) {
            return byFurnizorId;
        }
    }
    const code = String(tab.furnizorCode || '').trim().toLowerCase();
    if (code) {
        const byCode = rows.find(
            (row) => String(row?.furnizorCode || '').trim().toLowerCase() === code,
        );
        if (byCode) {
            return byCode;
        }
    }
    const clientKey = String(tab.clientKey || '').trim();
    if (clientKey) {
        const byKey = rows.find(
            (row) => String(row?.clientKey || '').trim() === clientKey,
        );
        if (byKey) {
            return byKey;
        }
    }
    const name = String(tab.furnizorName || '').trim().toLowerCase();
    if (name) {
        const byName = rows.find(
            (row) => String(row?.furnizorName || '').trim().toLowerCase() === name,
        );
        if (byName) {
            return byName;
        }
    }
    // Import often stores „producător ca furnizor” with empty furnizorName/code/id.
    // UI still shows manufacturerName as „Hirsch”, but identity fields are blank —
    // match on the flag (+ default) so adding Poliplast does not relabel Hirsch
    // as „Furnizor nou”.
    if (tab.usesManufacturerAsFurnizor) {
        const mfrRows = rows.filter((row) => Boolean(row?.usesManufacturerAsFurnizor));
        if (mfrRows.length === 1) {
            return mfrRows[0];
        }
        if (mfrRows.length > 1) {
            const byDefault = mfrRows.find(
                (row) => Boolean(row?.isDefault) === Boolean(tab.isDefault),
            );
            if (byDefault) {
                return byDefault;
            }
        }
    }
    return null;
}

/**
 * @param {Object|null|undefined} draftTab
 * @param {Object|null|undefined} liveTab
 * @returns {boolean}
 */
export function isFurnizorTabModified(draftTab, liveTab) {
    if (!draftTab || !liveTab) {
        return Boolean(draftTab);
    }

    // Only compare discounts when the draft/payload actually carries the array.
    // Payload from builder omits `discounts` (legacy fields are enough).
    if (Object.prototype.hasOwnProperty.call(draftTab, 'discounts')) {
        const draftDiscounts = Array.isArray(draftTab.discounts) ? draftTab.discounts : [];
        const liveDiscounts = Array.isArray(liveTab.discounts) ? liveTab.discounts : [];
        if (draftDiscounts.length !== liveDiscounts.length) {
            return true;
        }
        for (let i = 0; i < draftDiscounts.length; i += 1) {
            if (
                normalizeComparableNumber(draftDiscounts[i])
                !== normalizeComparableNumber(liveDiscounts[i])
            ) {
                return true;
            }
        }
    }

    return FURNIZOR_TAB_COMPARE_FIELDS.some((field) => {
        // Skip fields omitted from the draft/payload side — missing keys must not
        // count as edits vs live-only form leftovers (false „Actualizare”).
        if (!Object.prototype.hasOwnProperty.call(draftTab, field)) {
            return false;
        }
        // Legacy live rows (flat pricing_detail) often omit discount/markup keys;
        // do not treat payload defaults (0) as edits against missing live fields.
        if (
            !Object.prototype.hasOwnProperty.call(liveTab, field)
            && (liveTab[field] === undefined || liveTab[field] === null)
        ) {
            return false;
        }
        // Formula-auto transport % (green) is derived from the calculator — same as
        // markupActual. Comparing it invents Actualizare on every untouched sibling
        // that still has a formula rate after open→%→save round-trip.
        if (
            field === 'transportCostRate'
            && (
                isFormulaAutoTransportRate(draftTab)
                || isFormulaAutoTransportRate(liveTab)
            )
        ) {
            return false;
        }
        const left = draftTab[field];
        const right = liveTab[field];
        if (
            field === 'usesManufacturerAsFurnizor'
            || field === 'isDefault'
        ) {
            return Boolean(left) !== Boolean(right);
        }
        // Override value only counts when override is active on either side;
        // flag-only / leftover value noise must not invent Actualizare.
        if (field === 'overrideListPriceRonIncVatFinalValue') {
            if (
                !draftTab.overrideListPriceRonIncVatFinal
                && !liveTab.overrideListPriceRonIncVatFinal
            ) {
                return false;
            }
            return normalizeComparableNumber(left) !== normalizeComparableNumber(right);
        }
        if (
            field === 'currency'
            || field === 'furnizorCode'
            || field === 'furnizorName'
            || field === 'furnizorId'
        ) {
            const leftNorm = normalizeComparableScalar(left);
            const rightNorm = normalizeComparableScalar(right);
            // Incomplete identity on one side (legacy flat live vs payload code) is
            // not a user edit — matching already paired these tabs.
            if (
                (field === 'furnizorCode' || field === 'furnizorName' || field === 'furnizorId')
                && (!leftNorm || !rightNorm)
            ) {
                return false;
            }
            return leftNorm !== rightNorm;
        }
        if (FURNIZOR_TAB_RATIO_COMPARE_FIELDS.has(field)) {
            return !areComparableRatesEqual(left, right);
        }
        return normalizeComparableNumber(left) !== normalizeComparableNumber(right);
    });
}

/**
 * @param {Object|null|undefined} form
 * @param {Object|null|undefined} detail
 * @returns {Object[]}
 */
function resolveDraftFurnizorTabs(form, detail = null) {
    const fromForm = Array.isArray(form?.furnizorPricings) ? form.furnizorPricings : [];
    if (fromForm.length) {
        return fromForm;
    }
    return Array.isArray(detail?.furnizorPricings) ? detail.furnizorPricings : [];
}

/**
 * Picks furnizor tabs to show as separate "listă în lucru" rows:
 * new tabs, modified tabs, or all tabs on create. Falls back to default
 * when only product-level fields changed.
 *
 * Change detection compares payload furnizorPricings (API/ratio shape) to the
 * live product when available, so form percentage inputs are not compared to
 * stored ratios.
 *
 * @param {Object} draft
 * @param {Object|null} [liveItem]
 * @returns {Object[]}
 */
export function selectDraftFurnizorTabsForDisplay(draft, liveItem = null) {
    const builderState = draft?.builderState || null;
    const form = builderState?.productBuilderForm || null;
    const detail = draft?.payload?.pricing_detail || liveItem?.pricing_detail || null;
    const formTabs = resolveDraftFurnizorTabs(form, detail);

    if (!formTabs.length) {
        return [];
    }
    if (formTabs.length === 1) {
        return formTabs;
    }

    if (draft?.action === 'create') {
        return formTabs;
    }

    const liveTabs = resolveLiveFurnizorTabsForDraftCompare(liveItem);
    const payloadTabs = Array.isArray(draft?.payload?.pricing_detail?.furnizorPricings)
        ? draft.payload.pricing_detail.furnizorPricings
        : [];

    // Prefer payload↔live (same serialization). Fallback to form tabs when
    // payload has no nested furnizor rows yet.
    const compareTabs = payloadTabs.length ? payloadTabs : formTabs;
    const comparingPayload = payloadTabs.length > 0;

    const selectedCompareTabs = compareTabs.filter((tab) => {
        if (isUnpublishedNewFurnizorTab(draft, tab)) {
            return true;
        }
        if (draftHasUnpublishedNewFurnizor(draft)) {
            const formTwin = findMatchingLiveFurnizorTab(tab, formTabs) || tab;
            const payloadTwin = comparingPayload
                ? tab
                : findMatchingLiveFurnizorTab(formTwin, payloadTabs);
            const liveTwin = findMatchingLiveFurnizorTab(payloadTwin || formTwin, liveTabs);
            // Unflagged default tab with no live match is still a new supplier
            // (product originally had no furnizor; only Adaugă furnizor gets keys).
            if (!liveTwin) {
                return true;
            }
            return isExistingFurnizorChangedWithNewSuppliers(
                draft,
                liveItem,
                liveTwin,
                comparingPayload ? tab : payloadTwin,
                formTwin,
            );
        }
        const liveTab = findMatchingLiveFurnizorTab(tab, liveTabs);
        if (!liveTab) {
            return true;
        }
        // Form percentage fields vs live ratios → false positives; only compare payload.
        if (!comparingPayload) {
            return false;
        }
        return isFurnizorTabModified(tab, liveTab);
    });

    if (!selectedCompareTabs.length) {
        const defaultTab = formTabs.find((tab) => tab?.isDefault) || formTabs[0];
        return defaultTab ? [defaultTab] : [];
    }

    const selectedFormTabs = formTabs.filter((formTab) => (
        selectedCompareTabs.some((compareTab) => (
            findMatchingLiveFurnizorTab(formTab, [compareTab]) != null
            || (
                String(formTab?.clientKey || '').trim()
                && String(formTab.clientKey).trim() === String(compareTab?.clientKey || '').trim()
            )
        ))
    ));

    if (selectedFormTabs.length) {
        return selectedFormTabs;
    }

    // Payload tabs without matching form clientKeys — still show form defaults
    // for each selected compare tab by order when possible.
    return selectedCompareTabs.map((compareTab, index) => {
        const matched = findMatchingLiveFurnizorTab(compareTab, formTabs);
        return matched || formTabs[index] || formTabs[0];
    }).filter(Boolean);
}

/**
 * @param {Object|null|undefined} form
 * @param {Object|null|undefined} pricingSettings
 * @param {Object|null|undefined} [builderState]
 * @param {Object|null|undefined} [furnizorTab]
 * @returns {{
 *   entryPriceRonExVat: number|null,
 *   entryPriceRonExVatOld: number|null,
 *   markupReference: number|null,
 *   markupActual: number|null,
 *   listPriceRonIncVatReference: number|null,
 *   listPriceRonIncVatFinal: number|null,
 *   priceAdjustmentRatio: number|null,
 * }|null}
 */
export function computeBuilderDraftPricing(
    form,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
    builderState = null,
    furnizorTab = null,
) {
    if (!form || typeof form !== 'object') {
        return null;
    }

    const tab = furnizorTab
        || getDefaultFurnizorPricing(form)
        || form.furnizorPricings?.[0]
        || null;
    if (!tab) {
        return null;
    }

    const settings = pricingSettings || DEFAULT_PRICING_SETTINGS;
    const merged = mergeFormWithFurnizorPricing(form, tab);
    const calculatorInput = builderFormToCalculatorInput(merged);
    const computed = calculateProductPricing(calculatorInput, settings);

    const listReference = toNullableNumber(computed.listPriceRonIncVatReference);
    const listFinal = toNullableNumber(computed.listPriceRonIncVatFinal);
    const rawStoredAdjustment = merged.priceAdjustmentRatio;
    const hasStoredAdjustment = rawStoredAdjustment !== null
        && rawStoredAdjustment !== undefined
        && String(rawStoredAdjustment).trim() !== '';
    const storedAdjustment = hasStoredAdjustment
        ? toNullableNumber(calculatorInput.priceAdjustmentRatio)
        : null;
    const computedAdjustment = listReference > 0 && listFinal != null
        ? calculatePriceAdjustmentRatio(listReference, listFinal)
        : toNullableNumber(computed.computedPriceAdjustmentRatio);

    // Prefer frozen open-session (lista veche) baselines. After draft save,
    // supplier old is normalized to new, so the reference snapshot often equals
    // the current price — and last-în-lucru Valoare veche must not be used here.
    const baselineOld = resolveEntryPriceRonExVatOldFromBaselines(
        builderState,
        tab.clientKey,
    );
    const baselineListPrevious = resolveListPricePreviousFromBaselines(
        builderState,
        tab.clientKey,
    );

    return {
        entryPriceRonExVat: toNullableNumber(computed.entryPriceRonExVat),
        // Only frozen open-session baseline here; live/lista-veche fallbacks
        // are applied in resolveDraftTablePricing (reference snapshot often
        // equals current price after draft normalize).
        entryPriceRonExVatOld: baselineOld,
        markupReference: toNullableNumber(calculatorInput.markupReference),
        markupActual: toNullableNumber(computed.markupActual),
        listPriceRonIncVatReference: baselineListPrevious ?? listReference,
        listPriceRonIncVatFinal: listFinal,
        priceAdjustmentRatio: storedAdjustment != null ? storedAdjustment : computedAdjustment,
    };
}

/**
 * Clears old-price columns (UI shows „—” via formatRonPrice).
 *
 * @param {Object} pricing
 * @returns {Object}
 */
export function clearDraftPriorPriceColumns(pricing) {
    return {
        ...pricing,
        supplier_list_price_old: null,
        supplier_list_price_adjustment_ratio: null,
        entry_price_ron_inc_vat_old: null,
        entry_price_adjustment_ratio: null,
        previous_price: null,
        price_adjustment_ratio: null,
    };
}

/**
 * Resolves acquisition / sale / list pricing fields for a draft table row.
 *
 * @param {Object} draft
 * @param {Object|null} [liveItem]
 * @param {Object|null} [furnizorTab]
 * @returns {{
 *   entry_price_ron_inc_vat_old: number|null,
 *   entry_price_ron_inc_vat: number|null,
 *   entry_price_adjustment_ratio: number|null,
 *   markup_reference: number|null,
 *   markup_actual: number|null,
 *   previous_price: number|null,
 *   price: number|null,
 *   price_adjustment_ratio: number|null,
 *   supplier_list_price_old: number|null,
 *   supplier_list_price_new: number|null,
 *   supplier_list_price_adjustment_ratio: number|null,
 *   currency: string,
 *   is_default: boolean,
 * }}
 */
export function resolveDraftTablePricing(draft, liveItem = null, furnizorTab = null) {
    // Prefer published list price (lista veche) over any draft-cached previous.
    const previousSalePrice = toNullableNumber(
        liveItem?.price ?? draft?.previousPrice ?? draft?.summary?.previous_price,
    );
    const newSalePrice = toNullableNumber(
        draft?.summary?.price ?? draft?.payload?.price,
    );
    const noPriorListPrice = draftRowHasNoPriorListPrice(draft, liveItem, furnizorTab);

    const builderState = draft?.builderState || null;
    const form = builderState?.productBuilderForm || null;
    const liveDetail = liveItem?.pricing_detail || null;
    const liveTabs = Array.isArray(liveDetail?.furnizorPricings)
        ? liveDetail.furnizorPricings
        : [];
    const liveTab = furnizorTab
        ? findMatchingLiveFurnizorTab(furnizorTab, liveTabs)
        : (liveTabs.find((row) => row?.isDefault) || liveTabs[0] || null);
    const liveEntryPrice = toNullableNumber(
        liveItem?.entry_price_ron_ex_vat
            ?? liveDetail?.entryPriceRonExVat,
    );
    const liveTabSalePrice = toNullableNumber(
        liveTab?.listPriceRonIncVatFinal
            ?? liveTab?.manualListPriceRonIncVatFinal
            ?? liveTab?.overrideListPriceRonIncVatFinalValue,
    );
    const supplierFields = resolveSupplierListDisplayFields(
        furnizorTab,
        form,
        builderState,
        liveItem,
    );
    const builderPricing = computeBuilderDraftPricing(
        form,
        builderState?.pricingSettings,
        builderState,
        furnizorTab,
    );

    if (builderPricing) {
        const useProductLevelLive = !furnizorTab || Boolean(furnizorTab.isDefault);
        let liveComputedEntry = null;
        if (liveTab && form) {
            const settings = builderState?.pricingSettings || DEFAULT_PRICING_SETTINGS;
            const merged = mergeFormWithFurnizorPricing(form, liveTab);
            liveComputedEntry = toNullableNumber(
                calculateProductPricing(
                    builderFormToCalculatorInput(merged),
                    settings,
                ).entryPriceRonExVat,
            );
        }
        // Lista veche: frozen open baselines, then published live tab/item.
        const entryOld = noPriorListPrice
            ? null
            : (
                builderPricing.entryPriceRonExVatOld
                ?? liveComputedEntry
                ?? (useProductLevelLive ? liveEntryPrice : null)
            );
        const listPrevious = noPriorListPrice
            ? null
            : (
                liveTabSalePrice
                ?? builderPricing.listPriceRonIncVatReference
                ?? previousSalePrice
            );
        const listNew = builderPricing.listPriceRonIncVatFinal ?? newSalePrice;

        const previousPrice = noPriorListPrice
            ? null
            : (
                useProductLevelLive
                    ? (previousSalePrice ?? listPrevious)
                    : (listPrevious ?? previousSalePrice)
            );
        const price = listNew ?? newSalePrice;
        // Diferenta (ex-Majorare listă): old vs new sale price only.
        const listMajorare = previousPrice != null && price != null
            ? calculatePriceAdjustmentRatio(previousPrice, price)
            : (noPriorListPrice ? null : builderPricing.priceAdjustmentRatio);

        const entryNew = builderPricing.entryPriceRonExVat;

        const resolved = {
            entry_price_ron_inc_vat_old: entryOld,
            entry_price_ron_inc_vat: entryNew,
            entry_price_adjustment_ratio: calculatePricingValueChangeRatio(
                entryNew,
                entryOld,
            ),
            markup_reference: builderPricing.markupReference
                ?? toNullableNumber(liveItem?.markup_reference),
            markup_actual: builderPricing.markupActual
                ?? toNullableNumber(liveItem?.markup_actual),
            previous_price: previousPrice,
            price,
            price_adjustment_ratio: listMajorare,
            ...supplierFields,
        };
        return noPriorListPrice
            ? clearDraftPriorPriceColumns(resolved)
            : resolved;
    }

    const entryLive = toNullableNumber(
        liveItem?.entry_price_ron_ex_vat
            ?? liveItem?.pricing_detail?.entryPriceRonExVat,
    );
    const previousPrice = noPriorListPrice ? null : previousSalePrice;
    const price = newSalePrice;

    const resolved = {
        entry_price_ron_inc_vat_old: noPriorListPrice ? null : entryLive,
        entry_price_ron_inc_vat: entryLive,
        entry_price_adjustment_ratio: calculatePricingValueChangeRatio(
            entryLive,
            noPriorListPrice ? null : entryLive,
        ),
        markup_reference: toNullableNumber(liveItem?.markup_reference),
        markup_actual: toNullableNumber(liveItem?.markup_actual),
        previous_price: previousPrice,
        price,
        price_adjustment_ratio:
            previousPrice != null && price != null
                ? calculatePriceAdjustmentRatio(previousPrice, price)
                : null,
        ...supplierFields,
    };
    return noPriorListPrice
        ? clearDraftPriorPriceColumns(resolved)
        : resolved;
}

/**
 * @param {Object|null|undefined} pricing
 * @param {string} [manufacturerName]
 * @returns {string}
 */
export function resolveFurnizorPricingLabel(pricing, manufacturerName = '') {
    if (!pricing || typeof pricing !== 'object') {
        return String(manufacturerName || '').trim();
    }
    const manufacturer = String(manufacturerName || '').trim();
    if (pricing.usesManufacturerAsFurnizor) {
        return manufacturer
            || String(pricing.furnizorName || '').trim()
            || String(pricing.furnizorCode || '').trim()
            || 'Producător';
    }
    return String(pricing.furnizorName || '').trim()
        || String(pricing.furnizorCode || '').trim()
        || manufacturer;
}

/**
 * @param {Object|null|undefined} detail
 * @param {Object|null|undefined} liveItem
 * @param {Object|null|undefined} [furnizorTab]
 * @returns {string}
 */
export function resolveDraftFurnizorLabel(detail, liveItem = null, furnizorTab = null) {
    const manufacturerName = String(
        detail?.manufacturerName
            || liveItem?.pricing_detail?.manufacturerName
            || '',
    ).trim();

    if (furnizorTab) {
        return resolveFurnizorPricingLabel(furnizorTab, manufacturerName);
    }

    if (liveItem?.furnizor) {
        return String(liveItem.furnizor).trim();
    }
    const pricings = Array.isArray(detail?.furnizorPricings)
        ? detail.furnizorPricings
        : [];
    const defaultPricing = pricings.find((row) => row?.isDefault) || pricings[0];
    if (!defaultPricing) {
        return manufacturerName;
    }
    return resolveFurnizorPricingLabel(defaultPricing, manufacturerName);
}

/**
 * Resolves product-level notes for table display (not furnizor notes).
 *
 * @param {Object|null|undefined} itemOrDetail - product row or pricing_detail
 * @returns {string}
 */
export function resolveProductNotes(itemOrDetail) {
    if (!itemOrDetail || typeof itemOrDetail !== 'object') {
        return '';
    }
    const detail = itemOrDetail.pricing_detail || itemOrDetail;
    const candidates = [
        itemOrDetail.productNotes,
        itemOrDetail.product_notes,
        detail?.productNotes,
        detail?.product_notes,
    ];
    for (const candidate of candidates) {
        const text = String(candidate ?? '').trim();
        if (text) {
            return text;
        }
    }
    return '';
}

/**
 * @param {Object|null|undefined} draft
 * @param {Object|null|undefined} [liveItem]
 * @param {Object|null|undefined} [furnizorTab]
 * @returns {'new'|'copied'|'new_furnizor'|'update'}
 */
export function resolveDraftDisplayKind(
    draft,
    liveItem = null,
    furnizorTab = null,
) {
    const action = String(
        draft?.action || draft?.summary?.action || 'update',
    ).trim();

    if (action === 'create') {
        const productCode = String(
            draft?.payload?.product_code
                || draft?.summary?.product_code
                || '',
        ).trim();
        const sourceCode = String(
            draft?.sourceProductCode
                || draft?.builderState?.sessionSourceProductCode
                || draft?.payload?.old_product_code
                || '',
        ).trim();
        if (sourceCode && sourceCode !== productCode) {
            return 'copied';
        }
        return 'new';
    }

    // New supplier on an existing product → "Furnizor nou" (same as list compare).
    if (furnizorTab) {
        const tabKey = String(furnizorTab.clientKey || '').trim();
        const unpublished = draft?.builderState?.unpublishedNewFurnizorKeys;
        if (
            tabKey
            && unpublished
            && typeof unpublished === 'object'
            && unpublished[tabKey]
        ) {
            return 'new_furnizor';
        }
        // Need live furnizor rows to decide "new" vs "update". Missing liveItem
        // must not force new_furnizor (would wipe lista veche on ordinary updates).
        if (!liveItem) {
            return 'update';
        }
        const liveTabs = resolveLiveFurnizorTabsForDraftCompare(liveItem);
        if (liveTabs.length > 0 && !findMatchingLiveFurnizorTab(furnizorTab, liveTabs)) {
            return 'new_furnizor';
        }
    }

    return 'update';
}

/**
 * New product / new furnizor rows have no published „listă veche”.
 * Do not show open-session baselines copied from the source furnizor tab.
 *
 * @param {Object|null|undefined} draft
 * @param {Object|null|undefined} [liveItem]
 * @param {Object|null|undefined} [furnizorTab]
 * @returns {boolean}
 */
export function draftRowHasNoPriorListPrice(draft, liveItem = null, furnizorTab = null) {
    const action = String(
        draft?.action || draft?.summary?.action || 'update',
    ).trim();
    if (action === 'create') {
        return true;
    }
    const tabKey = String(furnizorTab?.clientKey || '').trim();
    const unpublished = draft?.builderState?.unpublishedNewFurnizorKeys;
    if (
        tabKey
        && unpublished
        && typeof unpublished === 'object'
        && unpublished[tabKey]
    ) {
        return true;
    }
    const kind = resolveDraftDisplayKind(draft, liveItem, furnizorTab);
    return kind === 'new' || kind === 'copied' || kind === 'new_furnizor';
}

/**
 * @param {Object|null|undefined} draft
 * @returns {string}
 */
export function resolveDraftSourceProductCode(draft) {
    const productCode = String(
        draft?.payload?.product_code
            || draft?.summary?.product_code
            || '',
    ).trim();
    const sourceCode = String(
        draft?.sourceProductCode
            || draft?.builderState?.sessionSourceProductCode
            || draft?.payload?.old_product_code
            || '',
    ).trim();
    if (sourceCode && sourceCode !== productCode) {
        return sourceCode;
    }
    return '';
}

/**
 * @param {Object|null|undefined} detail
 * @param {Object|null|undefined} liveItem
 * @param {string} detailKey
 * @param {string} liveKey
 * @param {{
 *   payloadHasPricingDetail?: boolean,
 *   draftProductCode?: string,
 *   liveProductCode?: string,
 * }} [options]
 * @param {Object|null|undefined} [furnizorTab]
 * @returns {string}
 */
export function resolveDraftManufacturerDisplayField(
    detail,
    liveItem,
    detailKey,
    liveKey,
    {
        payloadHasPricingDetail = false,
        draftProductCode = '',
        liveProductCode = '',
    } = {},
    furnizorTab = null,
) {
    if (
        furnizorTab
        && Object.prototype.hasOwnProperty.call(furnizorTab, detailKey)
    ) {
        return String(furnizorTab[detailKey] ?? '').trim();
    }

    if (
        payloadHasPricingDetail
        && detail
        && Object.prototype.hasOwnProperty.call(detail, detailKey)
    ) {
        return String(detail[detailKey] ?? '').trim();
    }

    const sameProduct = !liveProductCode
        || !draftProductCode
        || liveProductCode === draftProductCode;

    if (sameProduct) {
        const liveValue = liveItem?.[liveKey];
        if (liveValue != null && String(liveValue).trim()) {
            return String(liveValue).trim();
        }
    }

    if (detail?.[detailKey] != null && String(detail[detailKey]).trim()) {
        return String(detail[detailKey]).trim();
    }

    return '';
}

/**
 * Builds a draft display row shaped like the main products table (+ draft metadata).
 *
 * @param {Object} draft
 * @param {Object|null} [liveItem]
 * @param {Object|null} [furnizorTab]
 * @returns {Object}
 */
export function buildDraftDisplayRow(draft, liveItem = null, furnizorTab = null) {
    let pricing = resolveDraftTablePricing(draft, liveItem, furnizorTab);
    const payload = draft?.payload || {};
    const payloadHasPricingDetail = Boolean(payload.pricing_detail);
    const detail = payload.pricing_detail ?? liveItem?.pricing_detail ?? null;
    const productCode = String(
        draft?.summary?.product_code
            ?? payload.product_code
            ?? liveItem?.product_code
            ?? '',
    ).trim();
    const productName = String(
        draft?.summary?.product_name
            ?? payload.product_name
            ?? liveItem?.product_name
            ?? '',
    ).trim();
    const notes = resolveProductNotes({ pricing_detail: detail })
        || resolveProductNotes(liveItem);
    const tabKey = String(furnizorTab?.clientKey || furnizorTab?.id || '').trim();
    const rowId = tabKey
        ? `${draft?.id}::${tabKey}`
        : draft?.id;
    const manufacturerFieldOptions = {
        payloadHasPricingDetail,
        draftProductCode: String(payload.product_code || productCode || '').trim(),
        liveProductCode: String(liveItem?.product_code || '').trim(),
    };
    const draftKind = resolveDraftDisplayKind(draft, liveItem, furnizorTab);
    if (
        draftRowHasNoPriorListPrice(draft, liveItem, furnizorTab)
        || draftKind === 'new'
        || draftKind === 'copied'
        || draftKind === 'new_furnizor'
    ) {
        pricing = clearDraftPriorPriceColumns(pricing);
    }

    return {
        ...pricing,
        id: rowId,
        draft,
        furnizorTabKey: tabKey || null,
        action: draft?.action || 'update',
        updateMode: draft?.updateMode || draft?.summary?.update_mode || 'simple',
        serial_number: Boolean(
            detail?.serialNumber ?? liveItem?.serial_number,
        ),
        product_code: productCode,
        product_name: productName,
        status: payload.status ?? liveItem?.status ?? null,
        notes,
        draft_kind: draftKind,
        draft_source_product_code: resolveDraftSourceProductCode(draft),
        manufacturer_product_code: resolveDraftManufacturerDisplayField(
            detail,
            liveItem,
            'manufacturerProductCode',
            'manufacturer_product_code',
            manufacturerFieldOptions,
            furnizorTab,
        ),
        manufacturer_product_name: resolveDraftManufacturerDisplayField(
            detail,
            liveItem,
            'manufacturerProductName',
            'manufacturer_product_name',
            manufacturerFieldOptions,
            furnizorTab,
        ),
        measurement_unit:
            payload.measurement_unit
            ?? liveItem?.measurement_unit
            ?? '',
        furnizor: resolveDraftFurnizorLabel(detail, liveItem, furnizorTab),
        pricing_detail: detail,
        categories: payload.categories || liveItem?.categories || [],
        category_assignments:
            payload.category_assignments
            || liveItem?.category_assignments
            || [],
        systems: detail?.systems || liveItem?.systems || [],
        system1: detail?.system1 ?? liveItem?.system1,
        system2: detail?.system2 ?? liveItem?.system2,
        system3: detail?.system3 ?? liveItem?.system3,
    };
}

/**
 * Stable key for a draft furnizor tab (clientKey preferred, else id).
 *
 * @param {Object|null|undefined} tab
 * @returns {string}
 */
export function resolveDraftFurnizorTabKey(tab) {
    return String(tab?.clientKey || tab?.id || '').trim();
}

/**
 * @param {Object|null|undefined} draft
 * @returns {Record<string, boolean>}
 */
function getUnpublishedNewFurnizorKeySet(draft) {
    const fromUnpublished = draft?.builderState?.unpublishedNewFurnizorKeys;
    const fromPending = draft?.builderState?.newFurnizorKeysPendingFirstSave;
    const merged = {};
    if (fromUnpublished && typeof fromUnpublished === 'object') {
        Object.assign(merged, fromUnpublished);
    }
    if (fromPending && typeof fromPending === 'object') {
        Object.assign(merged, fromPending);
    }
    return merged;
}

/**
 * @param {Object|null|undefined} draft
 * @param {Object|null|undefined} tab
 * @returns {boolean}
 */
function isUnpublishedNewFurnizorTab(draft, tab) {
    const tabKey = resolveDraftFurnizorTabKey(tab);
    const unpublished = getUnpublishedNewFurnizorKeySet(draft);
    return Boolean(tabKey && unpublished[tabKey]);
}

/**
 * @param {Object|null|undefined} draft
 * @returns {boolean}
 */
function draftHasUnpublishedNewFurnizor(draft) {
    const unpublished = getUnpublishedNewFurnizorKeySet(draft);
    return Object.keys(unpublished).some((key) => Boolean(unpublished[key]));
}

/**
 * @param {Object|null|undefined} tab
 * @returns {boolean}
 */
function furnizorTabHasPersistedId(tab) {
    return tab?.id != null && String(tab.id).trim() !== '';
}

/**
 * Heuristic for „only added new supplier(s)” drafts when live cache / unpublished
 * keys are incomplete: mix of persisted ids + brand-new tabs without id.
 *
 * @param {Object|null|undefined} draft
 * @param {Object[]} formOrPayloadTabs
 * @returns {boolean}
 */
function draftLooksLikeAddFurnizorOnly(draft, formOrPayloadTabs = []) {
    if (draftHasUnpublishedNewFurnizor(draft)) {
        return true;
    }
    if (String(draft?.action || '').trim() === 'create') {
        return false;
    }
    const tabs = Array.isArray(formOrPayloadTabs) ? formOrPayloadTabs : [];
    if (tabs.length < 2) {
        return false;
    }
    const hasPersisted = tabs.some((tab) => furnizorTabHasPersistedId(tab));
    const hasBrandNew = tabs.some((tab) => !furnizorTabHasPersistedId(tab));
    return hasPersisted && hasBrandNew;
}

/**
 * True when supplier/entry old vs new columns differ (ignores sale-price recompute drift
 * and sub-cent rounding noise e.g. 9.99 vs 10).
 *
 * @param {Object} draft
 * @param {Object|null|undefined} liveItem
 * @param {Object} furnizorTab
 * @param {{ includeEntry?: boolean }} [options]
 * @returns {boolean}
 */
function draftExistingFurnizorHasVisiblePricingChange(
    draft,
    liveItem,
    furnizorTab,
    options = {},
) {
    const includeEntry = options.includeEntry !== false;
    // Without a live product, draft "old" fields (copied/normalized) are unreliable —
    // do not invent Actualizare from stale supplierListPriceOld on the payload.
    if (!liveItem) {
        if (!furnizorTab?.isDefault) {
            return false;
        }
        const prev = toNullableNumber(
            draft?.previousPrice ?? draft?.summary?.previous_price,
        );
        const next = toNullableNumber(
            draft?.summary?.price ?? draft?.payload?.price,
        );
        if (prev == null || next == null) {
            return false;
        }
        const abs = Math.abs(prev - next);
        const rel = abs / Math.max(Math.abs(prev), 1e-9);
        return abs > 0.05 && rel > 0.005;
    }

    const pricing = resolveDraftTablePricing(draft, liveItem, furnizorTab);
    // Same supplier/entry columns the draft table shows (0% → unchanged).
    // Do not use sale previous↔price here: fixtures and recompute drift often
    // differ while supplier/entry stay flat.
    // Entry includes transport — formula-auto rate drift invents Actualizare on
    // untouched siblings, so callers that only need "price edited" pass
    // includeEntry: false.
    const pairs = [
        [pricing.supplier_list_price_old, pricing.supplier_list_price_new],
    ];
    if (includeEntry) {
        pairs.push([
            pricing.entry_price_ron_inc_vat_old,
            pricing.entry_price_ron_inc_vat,
        ]);
    }
    for (const [oldVal, newVal] of pairs) {
        const oldNum = toNullableNumber(oldVal);
        const newNum = toNullableNumber(newVal);
        if (oldNum == null || newNum == null) {
            continue;
        }
        const abs = Math.abs(oldNum - newNum);
        if (abs <= 0.02) {
            continue;
        }
        const rel = abs / Math.max(Math.abs(oldNum), Math.abs(newNum), 1e-9);
        if (rel <= 0.002) {
            continue;
        }
        return true;
    }
    return false;
}

/**
 * Existing furnizor changed meaningfully while draft also adds new suppliers.
 * Uses table-visible supplier/entry old vs new (0% → unchanged), plus a real
 * override *value* change vs live sale — not a bare override flag mismatch.
 *
 * @param {Object} draft
 * @param {Object|null|undefined} liveItem
 * @param {Object|null|undefined} liveTab
 * @param {Object|null|undefined} payloadTab
 * @param {Object} formOrPayloadTab
 * @returns {boolean}
 */
function isExistingFurnizorChangedWithNewSuppliers(
    draft,
    liveItem,
    liveTab,
    payloadTab,
    formOrPayloadTab,
) {
    const tab = payloadTab || formOrPayloadTab;
    // Supplier list old≠new only. Entry old≠new drifts (FX / formula) when a
    // sibling furnizor is added and would invent Actualizare with no real edit.
    // Real input edits (incl. cost transport %) are caught vs live below.
    if (draftExistingFurnizorHasVisiblePricingChange(
        draft,
        liveItem,
        tab,
        { includeEntry: false },
    )) {
        return true;
    }
    // transportCostRate, discounts, markup, … vs live (not drifted entry columns).
    if (liveTab && tab && isFurnizorTabModified(tab, liveTab)) {
        return true;
    }
    // Override value actually moved the sale price vs live (flag-only noise ignored).
    if (liveTab && payloadTab && Boolean(payloadTab.overrideListPriceRonIncVatFinal)) {
        const draftSale = toNullableNumber(payloadTab.overrideListPriceRonIncVatFinalValue);
        const liveSale = toNullableNumber(
            liveTab.listPriceRonIncVatFinal
            ?? liveTab.overrideListPriceRonIncVatFinalValue
            ?? liveItem?.price,
        );
        if (draftSale != null && liveSale != null) {
            const abs = Math.abs(draftSale - liveSale);
            if (abs > 0.05) {
                const rel = abs / Math.max(Math.abs(liveSale), 1e-9);
                if (rel > 0.005) {
                    return true;
                }
            }
        }
    }
    return false;
}

/**
 * Whether a form/payload furnizor tab should appear as a meaningful draft row
 * (new or actually modified). Unchanged existing siblings return false so they
 * can stay collapsed.
 *
 * Rules:
 * - flagged new tab → visible (Furnizor nou)
 * - unflagged tab with no live match → visible (Furnizor nou): e.g. first
 *   supplier filled on a product that had none (default tab never enters
 *   unpublished keys; only Adaugă furnizor does)
 * - existing tab with visible old≠new prices → visible (Actualizare)
 * - existing tab with 0% / identical old&new → collapsed, no chip
 *
 * @param {Object|null|undefined} tab form or payload tab
 * @param {Object[]|null|undefined} liveTabs
 * @param {Object[]|null|undefined} [compareTabs] payload tabs (API shape)
 * @param {Object|null|undefined} [draft]
 * @param {Object[]|null|undefined} [allDraftTabs] form+payload tabs for heuristics
 * @param {Object|null|undefined} [liveItem] full live product (for price columns)
 * @returns {boolean}
 */
export function isDraftFurnizorTabNewOrModified(
    tab,
    liveTabs = [],
    compareTabs = null,
    draft = null,
    allDraftTabs = null,
    liveItem = null,
) {
    if (!tab) {
        return false;
    }
    if (isUnpublishedNewFurnizorTab(draft, tab)) {
        return true;
    }

    const payloadTabs = Array.isArray(compareTabs) ? compareTabs : [];
    const siblingTabs = Array.isArray(allDraftTabs) && allDraftTabs.length
        ? allDraftTabs
        : (payloadTabs.length ? payloadTabs : [tab]);

    const payloadTab = payloadTabs.length
        ? (
            findMatchingLiveFurnizorTab(tab, payloadTabs)
            || (
                (() => {
                    const liveHint = findMatchingLiveFurnizorTab(tab, liveTabs);
                    return liveHint
                        ? findMatchingLiveFurnizorTab(liveHint, payloadTabs)
                        : null;
                })()
            )
        )
        : null;

    const liveTab = findMatchingLiveFurnizorTab(payloadTab || tab, liveTabs)
        || findMatchingLiveFurnizorTab(tab, liveTabs);

    // Adding new supplier(s): existing tabs use core pricing inputs vs live
    // (not recomputed sale price — that drifted and invented Actualizare at 0%).
    if (draftHasUnpublishedNewFurnizor(draft)) {
        // No live match: brand-new tabs stay visible; persisted tabs collapse
        // unless they actually changed (same idea as draftLooksLikeAddFurnizorOnly).
        // Default first tab is never in unpublishedNewFurnizorKeys.
        if (!liveTab) {
            if (furnizorTabHasPersistedId(tab)) {
                return isExistingFurnizorChangedWithNewSuppliers(
                    draft,
                    liveItem,
                    null,
                    payloadTab,
                    tab,
                );
            }
            return true;
        }
        return isExistingFurnizorChangedWithNewSuppliers(
            draft,
            liveItem,
            liveTab,
            payloadTab,
            tab,
        );
    }

    // Add-furnizor without unpublished keys (id heuristic only).
    if (draftLooksLikeAddFurnizorOnly(draft, siblingTabs)) {
        if (!liveTab) {
            return !furnizorTabHasPersistedId(tab);
        }
        return isExistingFurnizorChangedWithNewSuppliers(
            draft,
            liveItem,
            liveTab,
            payloadTab,
            tab,
        );
    }

    if (!liveTab) {
        return true;
    }
    if (!payloadTab) {
        return false;
    }
    // Supplier list price only — entry old≠new drifts when formula transport
    // (green) recalculates on untouched siblings and would invent Actualizare.
    if (draftExistingFurnizorHasVisiblePricingChange(
        draft,
        liveItem,
        tab,
        { includeEntry: false },
    )) {
        return true;
    }
    if (liveTab && payloadTab && Boolean(payloadTab.overrideListPriceRonIncVatFinal)) {
        const draftSale = toNullableNumber(payloadTab.overrideListPriceRonIncVatFinalValue);
        const liveSale = toNullableNumber(
            liveTab.listPriceRonIncVatFinal
            ?? liveTab.overrideListPriceRonIncVatFinalValue
            ?? liveItem?.price,
        );
        if (draftSale != null && liveSale != null) {
            const abs = Math.abs(draftSale - liveSale);
            if (abs > 0.05) {
                const rel = abs / Math.max(Math.abs(liveSale), 1e-9);
                if (rel > 0.005) {
                    return true;
                }
            }
        }
    }
    return isFurnizorTabModified(payloadTab, liveTab);
}

/**
 * @param {Object[]} rows
 * @param {{ hasCollapsibleUnchanged?: boolean }} [meta]
 * @returns {Object[]}
 */
function annotateDraftMultiFurnizorRows(rows, meta = {}) {
    const list = Array.isArray(rows) ? rows : [];
    if (!list.length) {
        return list;
    }
    const hasCollapsible = Boolean(meta.hasCollapsibleUnchanged);
    const lastIndex = list.length - 1;
    return list.map((row, index) => {
        const isUnchangedChild = row?.draft_kind === 'unchanged';
        const annotated = {
            ...row,
            has_multiple_furnizori: hasCollapsible,
            is_multi_furnizor_child: hasCollapsible && isUnchangedChild,
            is_default: Boolean(row?.is_default),
        };
        if (!hasCollapsible || list.length < 2) {
            return annotated;
        }
        return {
            ...annotated,
            multi_furnizor_group_pos: index === 0
                ? 'start'
                : (index === lastIndex ? 'end' : 'middle'),
        };
    });
}

/**
 * Expands one draft into one or more table rows — one per furnizor tab that
 * should appear in „listă în lucru”.
 *
 * Meaningful rows (new / copied / new_furnizor / update) are always visible.
 * Unchanged existing furnizor siblings are included only when `expanded` is true
 * (collapsed by default), matching multi-furnizor collapse on the live list.
 *
 * @param {Object} draft
 * @param {Object|null} [liveItem]
 * @param {{ expanded?: boolean }} [options]
 * @returns {Object[]}
 */
export function buildDraftDisplayRows(draft, liveItem = null, options = {}) {
    const expanded = Boolean(options.expanded);
    const builderState = draft?.builderState || null;
    const form = builderState?.productBuilderForm || null;
    const detail = draft?.payload?.pricing_detail || liveItem?.pricing_detail || null;
    const formTabs = resolveDraftFurnizorTabs(form, detail);
    const selectedTabs = selectDraftFurnizorTabsForDisplay(draft, liveItem);

    if (!formTabs.length) {
        return annotateDraftMultiFurnizorRows(
            [buildDraftDisplayRow(draft, liveItem)],
            { hasCollapsibleUnchanged: false },
        );
    }

    if (draft?.action === 'create' || formTabs.length === 1) {
        const tabs = selectedTabs.length ? selectedTabs : formTabs;
        return annotateDraftMultiFurnizorRows(
            tabs.map((tab) => buildDraftDisplayRow(draft, liveItem, tab)),
            { hasCollapsibleUnchanged: false },
        );
    }

    const liveTabs = resolveLiveFurnizorTabsForDraftCompare(liveItem);
    const payloadTabs = Array.isArray(draft?.payload?.pricing_detail?.furnizorPricings)
        ? draft.payload.pricing_detail.furnizorPricings
        : [];
    const siblingTabsForHeuristic = payloadTabs.length ? payloadTabs : formTabs;
    const hasFurnizorLevelChanges = formTabs.some((tab) => (
        isDraftFurnizorTabNewOrModified(
            tab,
            liveTabs,
            payloadTabs,
            draft,
            siblingTabsForHeuristic,
            liveItem,
        )
    ));

    const selectedKeys = new Set(
        selectedTabs.map((tab) => resolveDraftFurnizorTabKey(tab)).filter(Boolean),
    );

    const allRows = formTabs.map((tab) => {
        const row = buildDraftDisplayRow(draft, liveItem, tab);
        const tabKey = resolveDraftFurnizorTabKey(tab);
        const isSelected = tabKey
            ? selectedKeys.has(tabKey)
            : selectedTabs.some((selected) => (
                findMatchingLiveFurnizorTab(tab, [selected]) != null
                || selected === tab
            ));

        if (hasFurnizorLevelChanges) {
            if (!isDraftFurnizorTabNewOrModified(
                tab,
                liveTabs,
                payloadTabs,
                draft,
                siblingTabsForHeuristic,
                liveItem,
            )) {
                return { ...row, draft_kind: 'unchanged' };
            }
            return row;
        }

        // Product-level-only edit: keep the fallback selected tab as update;
        // other existing furnizori are unchanged siblings (collapsible).
        if (!isSelected) {
            return { ...row, draft_kind: 'unchanged' };
        }
        return row;
    });

    const hasCollapsibleUnchanged = allRows.some((row) => row.draft_kind === 'unchanged');
    const meaningfulRows = allRows.filter((row) => row.draft_kind !== 'unchanged');
    const unchangedRows = allRows.filter((row) => row.draft_kind === 'unchanged');
    const visibleRows = (!expanded && hasCollapsibleUnchanged)
        ? meaningfulRows
        : [...meaningfulRows, ...unchangedRows];

    if (!visibleRows.length) {
        return annotateDraftMultiFurnizorRows(
            (selectedTabs.length ? selectedTabs : [formTabs[0]]).map((tab) => (
                buildDraftDisplayRow(draft, liveItem, tab)
            )),
            { hasCollapsibleUnchanged: false },
        );
    }

    return annotateDraftMultiFurnizorRows(visibleRows, { hasCollapsibleUnchanged });
}

/**
 * Whether the draft has multiple furnizor tabs so "produs implicit" can change.
 *
 * @param {Object|null|undefined} draft
 * @returns {boolean}
 */
export function draftCanChangeDefaultFurnizor(draft) {
    const tabs = draft?.builderState?.productBuilderForm?.furnizorPricings;
    return Array.isArray(tabs) && tabs.length > 1;
}

/**
 * Sets the given furnizor tab as default on a draft and refreshes payload price
 * / pricing_detail from the builder form.
 *
 * @param {Object} draft
 * @param {string} furnizorTabKey
 * @returns {Object|null} Updated draft clone, or null if change is not possible
 */
export function applyDraftDefaultFurnizor(draft, furnizorTabKey) {
    if (!draft || typeof draft !== 'object') {
        return null;
    }
    const key = String(furnizorTabKey || '').trim();
    if (!key) {
        return null;
    }
    const next = cloneDeep(draft);
    const form = next.builderState?.productBuilderForm;
    if (!form || typeof form !== 'object') {
        return null;
    }
    const tabs = Array.isArray(form.furnizorPricings) ? form.furnizorPricings : [];
    if (tabs.length < 2) {
        return null;
    }
    const target = tabs.find((row) => String(row?.clientKey || '').trim() === key);
    if (!target) {
        return null;
    }
    if (target.isDefault) {
        return next;
    }

    setDefaultFurnizorPricing(form, key);

    const settings = {
        ...DEFAULT_PRICING_SETTINGS,
        ...(next.builderState.pricingSettings || {}),
    };
    next.builderState.pricingSettings = settings;

    const defaultPricing = getDefaultFurnizorPricing(form);
    const priceSource = calculateProductPricing(
        builderFormToCalculatorInput(
            mergeFormWithFurnizorPricing(form, defaultPricing),
        ),
        settings,
    );
    const pricingDetail = builderFormToPricingDetailPayload(
        form,
        settings,
        priceSource,
        priceSource,
    );
    const nextPrice = toNullableNumber(priceSource.listPriceRonIncVatFinal);

    next.payload = {
        ...(next.payload || {}),
        price: nextPrice,
        pricing_detail: pricingDetail,
        pricing_settings: { ...settings },
    };
    if (next.summary && typeof next.summary === 'object') {
        next.summary = {
            ...next.summary,
            price: nextPrice,
        };
    }
    return next;
}
